import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { debounce } from "lodash";
import { useSelector } from "react-redux";

import { useTheme, Autocomplete, TextField, useMediaQuery } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import SoftBox from "../../components/SoftBox";
import SoftButton from "../../components/SoftButton";
import SoftInput from "../../components/SoftInput";
import SoftTypography from "../../components/SoftTypography";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import PageHeader from "../../components/PageHeader";

import { UseCompanyId, UseIsAdmin } from "../../hooks/auth";
import { UseUsersDictionary, UseCompanyWorkspaces } from "../../hooks/company";
import { ADD_USER_KPI } from "../../api/kpi";
import { isBidAccuracyKpi } from "../../helpers/scoreAggregate";
import { toErrorMessage } from "../../helpers/errorText";
import { generateUuid } from "../../utils/generateUuid";
import {
  BID_QUALITY_QUESTIONS,
  BID_QUALITY_POINTS_PER_YES,
  computeBidQualityScore,
} from "../../constants/bidQuality";

import { T, FONT, CARD_SX, MICRO_LABEL_SX, TONE, scoreBand, FIGURE_SX } from "./bidQualityTokens";
import { BidQualityTabs, SectionCard, Notice } from "./BidQualityChrome";

/** Same field styling KpiModal already uses, for visual parity with the rest of Scorekeeper. */
const useInputFieldStyle = (primaryColor) => ({
  borderRadius: "8px",
  border: `1px solid ${T.fieldLine}`,
  backgroundColor: "#F6F7F9 !important",
  color: "#1d2939 !important",
  "& .MuiInputBase-input::placeholder": { color: "#98A2B3 !important", opacity: 1 },
  "&.Mui-focused": { borderColor: primaryColor, boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}` },
});

const FIELD_LABEL_SX = {
  fontFamily: FONT,
  fontSize: "12.5px",
  fontWeight: 600,
  color: T.ink,
  display: "block",
  marginBottom: "6px",
};

/* -------------------------------------------------------------------------- */

/**
 * One half of a question's Yes/No segmented control. A real button with
 * role="radio" rather than an MUI Radio: the whole 44px-tall chip is the
 * target (a bare radio dot is ~20px, under the 44pt minimum and genuinely
 * hard to hit on a phone in a truck), and the selected side carries the
 * answer's colour, an icon AND the word — never colour alone.
 */
const ChoiceButton = ({ selected, tone, label, Icon, onClick, primaryColor }) => (
  <SoftBox
    component="button"
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    sx={{
      flex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      minWidth: "92px",
      minHeight: "44px",
      padding: "0 14px",
      cursor: "pointer",
      borderRadius: "10px",
      fontFamily: FONT,
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: 1,
      backgroundColor: selected ? tone.fill : T.surface,
      border: `1.5px solid ${selected ? tone.border : T.line}`,
      color: selected ? tone.ink : T.muted,
      transition: "background-color 180ms ease-out, border-color 180ms ease-out, color 180ms ease-out",
      "&:hover": {
        borderColor: selected ? tone.border : T.fieldLine,
        backgroundColor: selected ? tone.fill : T.field,
        color: selected ? tone.ink : T.ink2,
      },
      "&:focus-visible": { outline: `2px solid ${primaryColor}`, outlineOffset: "2px" },
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    }}
  >
    <Icon sx={{ fontSize: "1.15rem" }} />
    {label}
  </SoftBox>
);

/**
 * A numbered question row. Unanswered rows only turn red once the technician
 * has actually tried to submit — flagging every question the moment the page
 * opens would make an untouched form look broken.
 */
const QuestionRow = ({ index, question, value, invalid, onChange, primaryColor, isLast, rowRef }) => (
  <SoftBox
    ref={rowRef}
    sx={{
      display: "flex",
      flexDirection: { mini: "column", sm: "row" },
      alignItems: { mini: "stretch", sm: "center" },
      gap: { mini: "12px", sm: "20px" },
      padding: { mini: "16px 0", sm: "18px 0" },
      borderBottom: isLast ? "none" : `1px solid ${T.hairline}`,
    }}
  >
    <SoftBox sx={{ display: "flex", alignItems: "flex-start", gap: "12px", flex: 1, minWidth: 0 }}>
      <SoftBox
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: "24px",
          height: "24px",
          marginTop: "1px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: T.hairline,
          border: `1px solid ${invalid ? TONE.bad.border : T.line}`,
          color: invalid ? TONE.bad.ink : T.muted,
          ...FIGURE_SX,
          fontSize: "12px",
          fontWeight: 700,
        }}
      >
        {index + 1}
      </SoftBox>
      <SoftBox sx={{ minWidth: 0 }}>
        <SoftTypography
          id={`bq-q-${question.key}`}
          sx={{ fontFamily: FONT, fontSize: "14px", fontWeight: 600, color: T.ink, lineHeight: 1.4 }}
        >
          {question.title}
        </SoftTypography>
        <SoftTypography
          sx={{ fontFamily: FONT, fontSize: "12.5px", fontWeight: 400, color: T.muted, lineHeight: 1.5, mt: "2px" }}
        >
          {question.hint}
        </SoftTypography>
        {invalid && (
          <SoftTypography
            sx={{ fontFamily: FONT, fontSize: "12px", fontWeight: 600, color: TONE.bad.ink, mt: "6px" }}
          >
            Pick Yes or No to finish this one.
          </SoftTypography>
        )}
      </SoftBox>
    </SoftBox>

    <SoftBox
      role="radiogroup"
      aria-labelledby={`bq-q-${question.key}`}
      sx={{
        display: "flex",
        gap: "8px",
        flexShrink: 0,
        width: { mini: "100%", sm: "auto" },
        paddingLeft: { mini: "36px", sm: 0 },
      }}
    >
      <ChoiceButton
        selected={value === true}
        tone={TONE.good}
        label="Yes"
        Icon={CheckRoundedIcon}
        onClick={() => onChange(true)}
        primaryColor={primaryColor}
      />
      <ChoiceButton
        selected={value === false}
        tone={TONE.bad}
        label="No"
        Icon={CloseRoundedIcon}
        onClick={() => onChange(false)}
        primaryColor={primaryColor}
      />
    </SoftBox>
  </SoftBox>
);

/**
 * The live score rail. One segment per question, each worth
 * BID_QUALITY_POINTS_PER_YES — so the bar IS the scoring rule rather than a
 * decoration of it: five green segments is the 100% the rep is chasing.
 */
const ScoreRail = ({ answers }) => (
  <SoftBox sx={{ display: "flex", gap: "4px", marginTop: "14px" }} aria-hidden="true">
    {BID_QUALITY_QUESTIONS.map((q) => {
      const v = answers[q.key];
      let tone = { fill: T.hairline, border: T.line };
      if (v === true) tone = { fill: T.good, border: T.good };
      else if (v === false) tone = { fill: T.bad, border: T.bad };
      return (
        <SoftBox
          key={q.key}
          sx={{
            flex: 1,
            height: "8px",
            borderRadius: "999px",
            backgroundColor: tone.fill,
            border: `1px solid ${tone.border}`,
            transition: "background-color 220ms ease-out, border-color 220ms ease-out",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
      );
    })}
  </SoftBox>
);

/* -------------------------------------------------------------------------- */

/**
 * Native replacement for the GoHighLevel "Job Bid Quality" form. A
 * technician rates a sales rep's bid on 5 required Yes/No questions; the
 * score attributes to the SELECTED rep, not the person filling this out —
 * addEmployeeKpi already supports that (userId is the attribution target,
 * separate from the authenticated submitter).
 *
 * Laid out as a normal GMB page (PageHeader banner + cards on the page
 * canvas), not as a lone card floating mid-screen: the previous version read
 * as the embedded third-party form it replaced.
 */
const BidQualityForm = () => {
  const theme = useTheme();
  const { primary: { main: primaryColor } } = theme.palette;
  const inputFieldStyle = useInputFieldStyle(primaryColor);
  const navigate = useNavigate();
  const isMobile = useMediaQuery((t) => t.breakpoints.down("sm"));
  const isAdmin = UseIsAdmin();

  const companyId = UseCompanyId();
  const usersDict = UseUsersDictionary();
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const allWorkspaces = UseCompanyWorkspaces(true); // true = include sub-workspaces (Field Sales, In-House Sales, ...)

  const [salesRepId, setSalesRepId] = useState(null);
  const [jobName, setJobName] = useState("");
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState("");
  const [error, setError] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const repFieldRef = useRef(null);
  const jobFieldRef = useRef(null);
  const questionRefs = useRef({});

  // "Sales Rep" means anyone in the Sales workspace or one of its
  // sub-workspaces (Field Sales, In-House Sales, etc.) — not the whole
  // company roster. Matched by name rather than a hardcoded id so a newly
  // added Sales sub-workspace is picked up automatically.
  const salesWorkspaces = useMemo(
    () => (allWorkspaces || []).filter((w) => /sales/i.test(w?.name || "")),
    [allWorkspaces],
  );
  const workspaceNames = useMemo(() => {
    const map = {};
    salesWorkspaces.forEach((w) => {
      map[String(w._id)] = w.name;
    });
    return map;
  }, [salesWorkspaces]);
  const salesGroupIds = useMemo(
    () => new Set(salesWorkspaces.map((w) => String(w._id))),
    [salesWorkspaces],
  );

  const repOptions = useMemo(() => {
    return Object.entries(usersDict || {})
      .filter(([, u]) =>
        u && typeof u === "object" && !u.isArchived && u.isActive !== false && !u.isYeti && u.lastName !== "Yeti"
        && salesGroupIds.has(String(u.groupId)))
      .map(([id, u]) => ({
        id,
        label: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || id,
        groupId: u.groupId,
        image: u.image,
        workspaceName: workspaceNames[String(u.groupId)] || "",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [usersDict, salesGroupIds, workspaceNames]);

  const selectedRep = useMemo(
    () => repOptions.find((r) => r.id === salesRepId) || null,
    [repOptions, salesRepId],
  );

  // Resolve which workspace's Bid Accuracy "form" KPI this rep's score
  // belongs to (each Sales workspace has its own aggregate + form pair).
  // Resolved as soon as a rep is picked rather than only on submit, so a
  // misconfigured workspace is called out while there is still an empty form
  // to abandon — not after five answers have already been filled in.
  const formKpiId = useMemo(() => {
    if (!selectedRep) return null;
    const workspaceKpis = companyKpis?.[selectedRep.groupId] || [];
    return workspaceKpis.find((k) => isBidAccuracyKpi(k))?.parameters?.formKpiId || null;
  }, [companyKpis, selectedRep]);

  const score = useMemo(() => computeBidQualityScore(answers), [answers]);
  const answeredCount = BID_QUALITY_QUESTIONS.filter((q) => answers[q.key] !== undefined).length;
  const remaining = BID_QUALITY_QUESTIONS.length - answeredCount;
  const allAnswered = remaining === 0;
  const band = scoreBand(score);

  const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setSalesRepId(null);
    setJobName("");
    setAnswers({});
    setComments("");
    setError("");
    setAttempted(false);
  };

  /** Puts the technician on the first thing that still needs them. */
  const focusFirstProblem = () => {
    const firstUnanswered = BID_QUALITY_QUESTIONS.find((q) => answers[q.key] === undefined);
    let node = null;
    if (!selectedRep) node = repFieldRef.current;
    else if (!jobName.trim()) node = jobFieldRef.current;
    else if (firstUnanswered) node = questionRefs.current[firstUnanswered.key];
    if (node?.scrollIntoView) node.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = debounce(async () => {
    setError("");
    setAttempted(true);
    if (!selectedRep || !jobName.trim() || !allAnswered) {
      if (!selectedRep) setError("Pick the sales rep whose bid you are rating.");
      else if (!jobName.trim()) setError("Add the job name so this score can be traced back.");
      else setError(`Answer the last ${remaining} question${remaining === 1 ? "" : "s"} to submit.`);
      focusFirstProblem();
      return;
    }
    if (!formKpiId) {
      setError("This rep's workspace has no Bid Accuracy metric configured. Contact an admin.");
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Submitting...");
    try {
      const formData = new FormData();
      formData.append("amount", 1);
      formData.append("score", score);
      formData.append("jobName", jobName.trim());
      formData.append("salesRepName", selectedRep.label);
      BID_QUALITY_QUESTIONS.forEach((q) => formData.append(q.key, answers[q.key]));
      if (comments.trim()) formData.append("comments", comments.trim());

      // A fresh entryId per submission lets the server dedupe a retried
      // request (flaky connection, double-tap) instead of double-counting
      // the job's score — same guard useKpiSubmit's normal path relies on.
      const result = await ADD_USER_KPI({
        kpiId: formKpiId,
        companyId,
        userId: selectedRep.id,
        entryId: generateUuid(),
        data: formData,
      });
      if (result?.error) throw new Error(result.error.message);

      toast.success("Job Bid Quality submitted", { id: toastId });
      // A confirmation panel, not a silently cleared form: the technician
      // sees the score that was actually recorded and who it landed on
      // before choosing to rate another job. (The old version navigated to
      // the route it was already on, which changed nothing on screen.)
      setReceipt({ score, rep: selectedRep, jobName: jobName.trim() });
      resetForm();
    } catch (err) {
      toast.error(toErrorMessage(err, "Could not submit"), { id: toastId });
    } finally {
      setSubmitting(false);
    }
  }, 400);

  const pageMargin = { mini: "0.5rem 1rem 1rem 1rem", sm: "1rem 1.5rem 1.5rem 1.5rem" };

  /* --------------------------- submitted receipt --------------------------- */

  if (receipt) {
    const receiptBand = scoreBand(receipt.score);
    return (
      <>
        <PageHeader
          headerText="Job Bid Quality"
          subheaderText="Rate how well the sales rep set the technician up to do the job right."
          isMobile={isMobile}
          tabs={<BidQualityTabs active="form" />}
        />
        <SoftBox sx={{ margin: pageMargin, maxWidth: "620px" }}>
          <SoftBox sx={{ ...CARD_SX, padding: { mini: "24px 20px", sm: "32px" }, textAlign: "center" }}>
            <SoftBox
              sx={{
                width: "56px",
                height: "56px",
                margin: "0 auto",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: TONE.good.fill,
                color: TONE.good.ink,
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: "2rem" }} />
            </SoftBox>
            <SoftTypography
              sx={{ fontFamily: FONT, fontSize: "18px", fontWeight: 700, color: T.ink, mt: "16px", letterSpacing: "-0.01em" }}
            >
              Score recorded
            </SoftTypography>
            <SoftTypography
              sx={{ fontFamily: FONT, fontSize: "13.5px", fontWeight: 400, color: T.muted, mt: "6px", lineHeight: 1.5 }}
            >
              {receipt.jobName} is now on {receipt.rep.label}&apos;s Bid Accuracy.
            </SoftTypography>

            <SoftBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                marginTop: "20px",
                padding: "10px 18px",
                borderRadius: "12px",
                backgroundColor: receiptBand.fill,
                border: `1px solid ${receiptBand.border}`,
              }}
            >
              <SoftTypography sx={{ ...FIGURE_SX, fontSize: "28px", fontWeight: 700, color: receiptBand.ink, lineHeight: 1 }}>
                {receipt.score}%
              </SoftTypography>
              <SoftTypography sx={{ fontFamily: FONT, fontSize: "13px", fontWeight: 600, color: receiptBand.ink }}>
                {receiptBand.label}
              </SoftTypography>
            </SoftBox>

            <SoftBox
              sx={{
                display: "flex",
                flexDirection: { mini: "column", sm: "row" },
                gap: "10px",
                justifyContent: "center",
                marginTop: "26px",
              }}
            >
              <SoftButton
                variant="gradient"
                color="primary"
                onClick={() => setReceipt(null)}
                sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "10px", fontWeight: 600, minWidth: "170px" }}
              >
                Rate another job
              </SoftButton>
              {isAdmin && (
                <SoftButton
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate("/bid-quality/reviews")}
                  sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "10px", fontWeight: 600, minWidth: "170px" }}
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  View submissions
                </SoftButton>
              )}
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </>
    );
  }

  /* -------------------------------- the form ------------------------------- */

  const repInvalid = attempted && !selectedRep;
  const jobInvalid = attempted && !jobName.trim();

  const scorePanel = (
    <SoftBox sx={{ ...CARD_SX, padding: "20px" }}>
      <SoftTypography sx={{ ...MICRO_LABEL_SX, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Job score
      </SoftTypography>
      <SoftBox sx={{ display: "flex", alignItems: "baseline", gap: "10px", marginTop: "6px" }}>
        <SoftTypography
          aria-live="polite"
          sx={{ ...FIGURE_SX, fontSize: "40px", fontWeight: 700, color: allAnswered ? band.ink : T.ink, lineHeight: 1 }}
        >
          {score}%
        </SoftTypography>
        {allAnswered && (
          <SoftTypography sx={{ fontFamily: FONT, fontSize: "13px", fontWeight: 600, color: band.ink }}>
            {band.label}
          </SoftTypography>
        )}
      </SoftBox>

      <ScoreRail answers={answers} />

      <SoftTypography sx={{ ...MICRO_LABEL_SX, marginTop: "10px", lineHeight: 1.5 }}>
        {allAnswered
          ? `All ${BID_QUALITY_QUESTIONS.length} answered · each Yes is worth ${BID_QUALITY_POINTS_PER_YES}%`
          : `${answeredCount} of ${BID_QUALITY_QUESTIONS.length} answered · anything left blank scores as a No`}
      </SoftTypography>

      {selectedRep && (
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "16px",
            paddingTop: "16px",
            borderTop: `1px solid ${T.hairline}`,
          }}
        >
          <SoftProfile src={selectedRep.image} name={selectedRep.label} width="36px" height="36px" fullCover />
          <SoftBox sx={{ minWidth: 0 }}>
            <SoftTypography sx={{ fontFamily: FONT, fontSize: "13px", fontWeight: 600, color: T.ink, lineHeight: 1.3 }}>
              {selectedRep.label}
            </SoftTypography>
            <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "11.5px" }}>
              Credited in {selectedRep.workspaceName || "Sales"}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      )}

      {error && (
        <SoftBox sx={{ marginTop: "16px" }}>
          <Notice tone="bad">{error}</Notice>
        </SoftBox>
      )}

      <SoftButton
        variant="gradient"
        color="primary"
        onClick={handleSubmit}
        disabled={submitting}
        fullWidth
        sx={{
          fontFamily: FONT,
          textTransform: "none",
          padding: "12px",
          fontSize: "0.95rem",
          fontWeight: 600,
          borderRadius: "10px",
          marginTop: "18px",
        }}
      >
        {submitting ? "Submitting..." : "Submit job score"}
      </SoftButton>
      <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "11.5px", textAlign: "center", marginTop: "8px" }}>
        Scores can&apos;t be edited once they&apos;re in.
      </SoftTypography>
    </SoftBox>
  );

  return (
    <>
      <PageHeader
        headerText="Job Bid Quality"
        subheaderText="Rate how well the sales rep set the technician up to do the job right. Five questions, one score."
        isMobile={isMobile}
        tabs={<BidQualityTabs active="form" />}
      />

      <SoftBox sx={{ margin: pageMargin }}>
        <SoftBox
          sx={{
            display: "grid",
            gridTemplateColumns: { mini: "1fr", lg: "minmax(0, 1fr) 320px" },
            gap: { mini: "14px", sm: "20px" },
            alignItems: "start",
          }}
        >
          <SoftBox sx={{ display: "flex", flexDirection: "column", gap: { mini: "14px", sm: "20px" }, minWidth: 0 }}>
            <SectionCard title="The job" caption="Who bid it, and which job you're rating.">
              <SoftBox sx={{ display: "grid", gridTemplateColumns: { mini: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                <SoftBox ref={repFieldRef}>
                  <SoftTypography component="label" sx={FIELD_LABEL_SX}>
                    Sales rep <span style={{ color: TONE.bad.ink }}>*</span>
                  </SoftTypography>
                  <Autocomplete
                    options={repOptions}
                    getOptionLabel={(o) => o.label}
                    value={selectedRep}
                    onChange={(e, newValue) => setSalesRepId(newValue?.id ?? null)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderOption={(props, option) => (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <SoftBox component="li" {...props} key={option.id} sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <SoftProfile src={option.image} name={option.label} width="28px" height="28px" fullCover />
                        <SoftBox sx={{ minWidth: 0 }}>
                          <SoftTypography sx={{ fontFamily: FONT, fontSize: "13.5px", fontWeight: 500, color: T.ink }}>
                            {option.label}
                          </SoftTypography>
                          {option.workspaceName && (
                            <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "11.5px" }}>
                              {option.workspaceName}
                            </SoftTypography>
                          )}
                        </SoftBox>
                      </SoftBox>
                    )}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        placeholder="Search sales reps"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            ...inputFieldStyle,
                            ...(repInvalid && { borderColor: TONE.bad.border }),
                            "& fieldset": { border: "none" },
                            padding: "2px 10px",
                            minHeight: "44px",
                          },
                        }}
                      />
                    )}
                  />
                  {repInvalid && (
                    <SoftTypography sx={{ fontFamily: FONT, fontSize: "12px", fontWeight: 600, color: TONE.bad.ink, mt: "6px" }}>
                      Pick the rep this score belongs to.
                    </SoftTypography>
                  )}
                  {!repInvalid && selectedRep && !formKpiId && (
                    <SoftBox sx={{ marginTop: "8px" }}>
                      <Notice tone="warn" title="No Bid Accuracy metric here">
                        {selectedRep.workspaceName || "This rep's workspace"} has no Bid Accuracy metric configured, so this
                        score has nowhere to land. Ask an admin to set one up, or pick a different rep.
                      </Notice>
                    </SoftBox>
                  )}
                </SoftBox>

                <SoftBox ref={jobFieldRef}>
                  <SoftTypography component="label" sx={FIELD_LABEL_SX}>
                    Job name <span style={{ color: TONE.bad.ink }}>*</span>
                  </SoftTypography>
                  <SoftInput
                    placeholder="e.g. Ramirez — water heater swap"
                    value={jobName}
                    onChange={(e) => setJobName(e.target.value)}
                    sx={{
                      ...inputFieldStyle,
                      minHeight: "44px",
                      ...(jobInvalid && { borderColor: TONE.bad.border }),
                    }}
                  />
                  <SoftTypography
                    sx={{
                      fontFamily: FONT,
                      fontSize: "11.5px",
                      fontWeight: jobInvalid ? 600 : 500,
                      color: jobInvalid ? TONE.bad.ink : T.muted,
                      marginTop: "6px",
                      lineHeight: 1.5,
                    }}
                  >
                    {jobInvalid
                      ? "Add the job name so this score can be traced back."
                      : "However you'd find it in the schedule — customer name works."}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>

              {salesGroupIds.size === 0 && (
                <SoftBox sx={{ marginTop: "16px" }}>
                  <Notice tone="warn" title="No Sales workspace found">
                    Nobody can be rated until a Sales workspace exists for this company. Contact an admin.
                  </Notice>
                </SoftBox>
              )}
            </SectionCard>

            <SectionCard
              title={`The ${BID_QUALITY_QUESTIONS.length} checks`}
              caption={`Every Yes adds ${BID_QUALITY_POINTS_PER_YES}% to the rep's score for this job.`}
              action={
                <SoftTypography sx={{ ...MICRO_LABEL_SX, ...FIGURE_SX, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {answeredCount}/{BID_QUALITY_QUESTIONS.length}
                </SoftTypography>
              }
              bodySx={{ padding: { mini: "4px 16px", sm: "4px 20px" } }}
            >
              {BID_QUALITY_QUESTIONS.map((q, i) => (
                <QuestionRow
                  key={q.key}
                  index={i}
                  question={q}
                  value={answers[q.key]}
                  invalid={attempted && answers[q.key] === undefined}
                  onChange={(v) => setAnswer(q.key, v)}
                  primaryColor={primaryColor}
                  isLast={i === BID_QUALITY_QUESTIONS.length - 1}
                  rowRef={(node) => {
                    questionRefs.current[q.key] = node;
                  }}
                />
              ))}
            </SectionCard>

            <SectionCard title="Anything worth adding?" caption="Optional — context an admin would want when they read this back.">
              <SoftInput
                placeholder="What went wrong, or what the rep nailed..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                multiline
                rows={3}
                sx={inputFieldStyle}
              />
            </SectionCard>
          </SoftBox>

          <SoftBox sx={{ minWidth: 0 }}>{scorePanel}</SoftBox>
        </SoftBox>
      </SoftBox>
    </>
  );
};

export default BidQualityForm;
